import React, { useState } from "react";
import Button from "@/components/settings/Button";
import { PDF_PAGE as PAGE, keepBlocks, pageSlices } from "@/components/viewerHours/pdfPages";
import { t } from "@/localization";

// Event Analytics' PDF: the report in its print layout (EventAnalytics
// `printing`: no switches, every metric), on US Letter pages cut between rows,
// cards and charts (pdfPages.js), with the EventLive logo at the top right of
// every page.

// Canvas pixels for the whole report: Safari's limit is 16.7 million.
const MAX_PIXELS = 15e6;

const nextFrame = () => new Promise((resolve) => requestAnimationFrame(() => resolve()));

const loadImage = (src) =>
  new Promise((resolve) => {
    if (!src) return resolve(null);
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = src;
  });

// The marked blocks (data-pdf="keep" | "next" | "logo"), in px from the report's top.
const blockRects = (root) => {
  const top = root.getBoundingClientRect().top;
  return Array.from(root.querySelectorAll("[data-pdf]")).map((el) => {
    const r = el.getBoundingClientRect();
    return { top: r.top - top, bottom: r.bottom - top, next: el.getAttribute("data-pdf") === "next" };
  });
};

const exportReportPdf = async (root, filename) => {
  const [{ default: html2canvas }, { jsPDF }] = await Promise.all([import("html2canvas"), import("jspdf")]);
  const width = root.offsetWidth;
  const height = root.scrollHeight;
  const scale = Math.min(2, Math.sqrt(MAX_PIXELS / (width * height)));
  const canvas = await html2canvas(root, {
    scale,
    useCORS: true,
    backgroundColor: "#ffffff",
    logging: false,
    // html2canvas draws react-native-web's images (a background-size: contain
    // div) with a stray strip of the picture above them: draw their <img>.
    onclone: (doc) =>
      doc.querySelectorAll("img").forEach((img) => {
        const background = img.previousElementSibling;
        if (!background || !background.style.backgroundImage) return;
        background.style.backgroundImage = "none";
        img.style.opacity = "1";
        img.style.zIndex = "0";
      }),
  });
  const mmPerPx = (PAGE.width - 2 * PAGE.margin) / width;
  const pageHeight = (PAGE.height - PAGE.contentTop - PAGE.margin) / mmPerPx;
  const slices = pageSlices(canvas.height / scale, pageHeight, keepBlocks(blockRects(root)));
  // The report's own logo (Powered by EventLive), for every page's header.
  const logo = await loadImage(root.querySelector('[data-pdf="logo"] img')?.src);

  const pdf = new jsPDF({ unit: "mm", format: "letter", orientation: "portrait", compress: true });
  slices.forEach(([start, end], i) => {
    if (i) pdf.addPage();
    const page = document.createElement("canvas");
    page.width = canvas.width;
    page.height = Math.max(1, Math.round((end - start) * scale));
    const ctx = page.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, page.width, page.height);
    ctx.drawImage(canvas, 0, Math.round(start * scale), page.width, page.height, 0, 0, page.width, page.height);
    pdf.addImage(page.toDataURL("image/jpeg", 0.92), "JPEG", PAGE.margin, PAGE.contentTop, PAGE.width - 2 * PAGE.margin, (end - start) * mmPerPx);
    if (logo) {
      const logoWidth = PAGE.logoHeight * (logo.naturalWidth / logo.naturalHeight);
      pdf.addImage(logo, "PNG", PAGE.width - PAGE.margin - logoWidth, PAGE.logoTop, logoWidth, PAGE.logoHeight);
    }
  });
  pdf.save(filename);
};

// onPrinting(true) puts the report in its print layout; it's back to the
// screen's when the PDF is saved (or fails).
const PDFExportButton = ({ event, contentRef, onPrinting }) => {
  const [busy, setBusy] = useState(false);

  const handleExportPDF = async () => {
    if (!contentRef?.current || busy) return;
    setBusy(true);
    onPrinting?.(true);
    try {
      await nextFrame();
      await nextFrame();
      await document.fonts?.ready;
      await exportReportPdf(
        contentRef.current,
        event?.title ? t("{title}-report.pdf", { title: event.title }) : t("analytics-report.pdf")
      );
    } catch (err) {
      console.error("Failed to generate PDF:", err);
    } finally {
      onPrinting?.(false);
      setBusy(false);
    }
  };

  return (
    <Button
      text={t("Export PDF")}
      icon="download-outline"
      size="sm"
      fullWidth={false}
      loading={busy}
      onPress={handleExportPDF}
    />
  );
};

export default PDFExportButton;
